import React, { FC } from 'react';
import {
  Box,
  Text,
  Heading,
  Avatar,
  AvatarSize,
  Separator,
  SeparatorType,
  ButtonAppearance,
  ButtonSize,
  Icon,
  IconSize,
  useModalState,
  Modal,
  ModalHeader,
  ModalClose,
  ModalBody,
  ModalFooter,
  ModalSize,
  ModalSubHeader,
} from '@bechtel/gds';
import { ActivityProps, BlockCommentsHistoryProps, HistoryItemProps } from './types';
import {
  FinalActivity,
  ActivityIconPattern,
  CommentStyled,
  ArrowStyled,
  AttachmentsCountStyled,
  ReasonAlignmentStyled,
  AttachmentButton,
  PipeCleaninessStyled,
} from './styled';
import { useDate } from '~/hooks/use-date';
import { map, join, orderBy } from 'lodash';
import { useName } from '~/hooks/use-name';
import { ImageViewer, useImageViewerState } from '~/components/organisms/image-viewer';
import {
  MilestoneStatusMessages,
  PersonaMessages,
  MilestoneActivityMessages,
  WeldStatusMessages,
  CommentHistory,
  FooterButtonStyled,
  TitleValue,
} from '~/components';
import { useLingui } from '@lingui/react';
import { Trans, t } from '@lingui/macro';
import { WeldMilestoneType } from '~/types';
import { EllipsisPlacement, TextWithEllipsis } from '~/components/atoms/text-with-ellipsis';
import {
  CommodityStatusCode,
  MilestoneActivity,
  MilestoneStatusCode,
  RejectWeldSubReason,
  RejectWeldReason,
  BlockStatusCode,
} from '@bechtel/types';

export const HistoryItem: FC<HistoryItemProps> = ({
  milestoneStatus,
  activity,
  attachments,
  isWeldCommodity,
  milestoneName,
  crewMember,
  last,
  id,
  dateTime,
  comments,
  rejectionReason,
  rejectionSubReasons,
  currentItrStatus,
  blockComments,
  workPackageTag,
  commodityTag,
  commodityName,
}) => {
  const { formatISODate, formatTime } = useDate();
  const { getFullName } = useName();
  const time = formatTime(dateTime);
  const day = formatISODate(dateTime);
  const { i18n } = useLingui();
  const isFitupMilestone = milestoneName?.toUpperCase() === WeldMilestoneType.FITUP;
  const { open: openImageViewer, close: closeImageViewer, visible: visibleImageViewer } = useImageViewerState(false);
  const { open: openCommentsModal, close: closeCommentsModal, visible: visibleCommentsModal } = useModalState(false);

  const milestoneStatusText =
    milestoneStatus &&
    (milestoneStatus == MilestoneStatusCode.REJECTED && isWeldCommodity
      ? i18n._(
          WeldStatusMessages[
            isFitupMilestone ? CommodityStatusCode.SENT_BACK_TO_PIPING : CommodityStatusCode.VT_SENT_BACK
          ]
        )
      : i18n._(MilestoneStatusMessages[milestoneStatus]));
  const milestoneActivityText =
    activity &&
    (activity == MilestoneActivity.REJECTED && isWeldCommodity
      ? i18n._(
          WeldStatusMessages[
            isFitupMilestone ? CommodityStatusCode.SENT_BACK_TO_PIPING : CommodityStatusCode.VT_SENT_BACK
          ]
        )
      : i18n._(MilestoneActivityMessages[activity]));
  const otherSubReason = join(
    map(rejectionSubReasons, (item) => {
      return item.reason == RejectWeldSubReason.OTHER_SUB_REASON ? item.comments : i18n._(item.reason);
    }),
    ', '
  );

  const pipeCleanInspectionTooltipValue = currentItrStatus
    ? `${getFullName(
        currentItrStatus?.pipeCleanInspectionBy?.firstName,
        currentItrStatus?.pipeCleanInspectionBy?.lastName
      )}\n${formatISODate(currentItrStatus?.pipeCleanInspectionDate)}\n${formatTime(
        currentItrStatus?.pipeCleanInspectionDate
      )}`
    : '';
  return (
    <Box mb={{ sm: '600' }} data-testid={`commodity-activity-item-${id}`}>
      {/* Min width is required to align the content vertically as same text length creating different width in the DOM */}
      <Box
        flexDirection={{ sm: 'column' }}
        alignItems={{ sm: 'flex-end' }}
        mt={{ sm: '400' }}
        pl={{ sm: '400' }}
        style={{ width: '112px' }}
      >
        {activity && (
          <Box mb={{ sm: '200' }}>
            <Text size="300" weight="600" color="grey.1000">
              {milestoneActivityText}
            </Text>
            &nbsp;
            <Text size="300" color="grey.1000">
              <Trans>on</Trans>
            </Text>
          </Box>
        )}
        <Box mb={{ sm: '200' }}>
          <Text size="250" color="grey.600">
            {time && time.toLowerCase()}
          </Text>
        </Box>
        <Text size="250" color="grey.600">
          {day}
        </Text>
      </Box>
      <Box flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }} mt={{ sm: '200' }} mx={{ sm: '400' }}>
        <Box mb={{ sm: '300' }}>
          <ActivityIconPattern status={milestoneStatus} />
        </Box>
        {/*  @ts-ignore: GDS typing update needed */}
        <Separator as="div" size="95%" borderWidth="1px" color="bechtel.blue.700" type={SeparatorType.VERTICAL} />
        {last && (
          <Box>
            <FinalActivity />
          </Box>
        )}
      </Box>
      <Box flexDirection={{ sm: 'column' }}>
        <Box mt={{ sm: '400' }}>
          <Text size="300" weight="600" color="grey.1000">
            {milestoneName}
          </Text>
          {milestoneName ? (
            <Text size="300" color="grey.600" transform="capitalize">
              :&nbsp;
              {milestoneStatusText}
            </Text>
          ) : (
            <Text size="400" color="grey.800" weight="600" family="graphik" transform="capitalize">
              {milestoneStatusText}
            </Text>
          )}
        </Box>
        {currentItrStatus && (
          <Box mt={{ sm: '450' }} mr={{ sm: '500' }}>
            <PipeCleaninessStyled>
              <Icon icon="circle-check" size={IconSize.XSMALL} />
              <Box ml={{ sm: '200' }}>
                <TextWithEllipsis
                  family="graphik"
                  color="green.800"
                  size="200"
                  ellipsisPlacement={EllipsisPlacement.END}
                  title={pipeCleanInspectionTooltipValue}
                >
                  <Trans>Pipe Cleanliness</Trans>
                </TextWithEllipsis>
              </Box>
            </PipeCleaninessStyled>
          </Box>
        )}
        <Box mt={{ sm: '400' }}>
          <Box mr={{ sm: '400' }}>
            <Avatar src={crewMember?.imageUrl} size={AvatarSize.MEDIUM} />
          </Box>
          <Box flexDirection={{ sm: 'column' }} justifyContent={{ sm: 'space-around' }}>
            <Text size="250" color="grey.600">
              {getFullName(crewMember?.firstName, crewMember?.lastName)}
            </Text>
            <Text size="250" color="grey.600">
              {i18n._(PersonaMessages[crewMember?.persona])}
            </Text>
          </Box>
          {!!attachments?.length && (
            <Box ml={{ sm: '550' }}>
              <AttachmentsCountStyled alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }}>
                {attachments?.length}
              </AttachmentsCountStyled>
              <AttachmentButton
                appearance={ButtonAppearance.SECONDARY}
                size={ButtonSize.SMALL}
                onClick={() => {
                  openImageViewer();
                }}
              >
                <Icon icon="paperclip" size={IconSize.MEDIUM} />
              </AttachmentButton>
            </Box>
          )}
          {milestoneStatus === MilestoneStatusCode.BLOCKED && blockComments && !!blockComments.length && (
            <Box ml={attachments && attachments?.length > 0 ? { sm: '100' } : { sm: '550' }}>
              <AttachmentsCountStyled alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }}>
                {blockComments.length}
              </AttachmentsCountStyled>
              <AttachmentButton
                appearance={ButtonAppearance.SECONDARY}
                size={ButtonSize.SMALL}
                onClick={() => {
                  openCommentsModal();
                }}
              >
                <Icon icon="comment-alt" iconStyle="solid" size={IconSize.MEDIUM} />
              </AttachmentButton>
            </Box>
          )}
        </Box>
        {visibleImageViewer && (
          <ImageViewer
            selectedImageIndex={0}
            images={map(attachments, 'url')}
            visible={visibleImageViewer}
            closable={true}
            onDismiss={closeImageViewer}
          />
        )}
        {visibleCommentsModal && (
          <BlockCommentsHistory
            blockComments={blockComments && blockComments.length > 0 ? blockComments : []}
            blockStatus={BlockStatusCode.BLOCK_OPENED}
            onClose={closeCommentsModal}
            commodityTag={commodityTag}
            workPackageTag={workPackageTag}
            commodityName={commodityName}
            milestoneName={milestoneName}
          />
        )}

        {rejectionReason?.reason !== RejectWeldReason.OTHER_REASON && comments && (
          <Box mt={{ sm: '400' }} mr={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
            <ArrowStyled />
            <CommentStyled style={{ width: '200px', wordBreak: 'keep-all' }}>
              <TextWithEllipsis title={comments}>{comments}</TextWithEllipsis>
            </CommentStyled>
          </Box>
        )}

        {rejectionReason && (
          <Box mt={{ sm: '400' }} mr={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
            <ArrowStyled />
            <CommentStyled>
              <ReasonAlignmentStyled>
                <Text size="250" color="grey.1000" weight="600">
                  <Trans>Reason:</Trans> &nbsp;
                </Text>
                <TextWithEllipsis
                  size="250"
                  title={
                    rejectionReason?.reason == RejectWeldReason.OTHER_REASON
                      ? rejectionReason.comments
                      : i18n._(rejectionReason?.reason)
                  }
                >
                  {rejectionReason?.reason == RejectWeldReason.OTHER_REASON
                    ? rejectionReason.comments
                    : i18n._(rejectionReason?.reason)}
                </TextWithEllipsis>
              </ReasonAlignmentStyled>
              {rejectionSubReasons && (
                <ReasonAlignmentStyled pt={{ sm: '300' }}>
                  <Text size="250" color="grey.1000" weight="600" style={{ whiteSpace: 'nowrap' }}>
                    <Trans> Sub Reason(s):</Trans>&nbsp;
                  </Text>
                  <TextWithEllipsis size="250" title={otherSubReason}>
                    {otherSubReason}
                  </TextWithEllipsis>
                </ReasonAlignmentStyled>
              )}
            </CommentStyled>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const BlockCommentsHistory: FC<BlockCommentsHistoryProps> = ({
  blockComments,
  blockStatus,
  onClose,
  workPackageTag,
  commodityTag,
  commodityName,
  milestoneName,
}) => {
  const orderedComments = orderBy(blockComments, ['createdAt'], ['desc']);
  return (
    <Modal visible size={ModalSize.DEFAULT} style={{ width: '552px', height: '552px' }}>
      <ModalHeader title={t`Comments`}>
        <ModalClose onClick={onClose}></ModalClose>
      </ModalHeader>
      <ModalSubHeader>
        <Box mt={{ sm: '400' }}>
          <TitleValue title={t`Work Package`} value={workPackageTag} size="500" />
        </Box>
        {commodityName && commodityTag && (
          <Box mt={{ sm: '400' }}>
            <TitleValue title={commodityName} value={commodityTag} size="500" />
          </Box>
        )}
        {milestoneName && (
          <Box mt={{ sm: '400' }}>
            <TitleValue title={t`Milestone`} value={milestoneName} size="500" />
          </Box>
        )}
      </ModalSubHeader>
      <ModalBody>
        <CommentHistory blockComments={orderedComments} blockStatus={blockStatus} canEditBlock={false} />
      </ModalBody>
      <ModalFooter>
        <Box justifyContent={{ sm: 'flex-end' }}>
          <Box mr={{ sm: '400' }}>
            <FooterButtonStyled appearance={ButtonAppearance.SECONDARY} onClick={onClose}>
              <Trans>Close</Trans>
            </FooterButtonStyled>
          </Box>
        </Box>
      </ModalFooter>
    </Modal>
  );
};

export const ActivityHistory: FC<ActivityProps> = ({
  activityTitle,
  milestonesActivityHistory,
  isWeldCommodity,
  workPackageTag,
  commodityTag,
  commodityName,
}) => {
  return (
    <>
      {activityTitle && (
        <Box m={{ sm: '500' }}>
          <Heading color="grey.1000" weight="600" data-testid="commodity-activity-header">
            {activityTitle}
          </Heading>
        </Box>
      )}
      <Box alignItems={{ sm: 'center' }} flexDirection={{ sm: 'column' }}>
        <Box flexDirection={{ sm: 'column' }} mt={{ sm: '500' }} data-testid="commodity-activity-history">
          {map(milestonesActivityHistory, (activity, index: number) => (
            <HistoryItem
              key={activity.id}
              {...activity}
              last={milestonesActivityHistory.length === index + 1}
              isWeldCommodity={isWeldCommodity}
              commodityName={commodityName}
              workPackageTag={workPackageTag}
              commodityTag={commodityTag}
            />
          ))}
        </Box>
      </Box>
    </>
  );
};
